import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import { AppDispatch } from "@/app/store";
import { ProductStoreOrderListItem } from "../api/fetchProductStoreOrders/types";
import { UpdatableOrderStatus } from "../api/updateProductStoreOrderStatus/types";
import { updateProductStoreOrderStatusAction } from "../slices/productStoreOrdersSlice";

interface PendingTransition {
  order: ProductStoreOrderListItem;
  target: UpdatableOrderStatus;
}

export function useOrderStatusTransition() {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const [pending, setPending] = useState<PendingTransition | null>(null);
  const [updating, setUpdating] = useState(false);

  const requestStatusUpdate = useCallback(
    (order: ProductStoreOrderListItem, target: UpdatableOrderStatus) => {
      setPending({ order, target });
    },
    [],
  );

  const cancelStatusUpdate = useCallback(() => {
    if (updating) return;
    setPending(null);
  }, [updating]);

  const confirmStatusUpdate = useCallback(async () => {
    if (!pending) return;
    setUpdating(true);
    try {
      await dispatch(
        updateProductStoreOrderStatusAction(pending.order.uuid, pending.target),
      );
      toast({
        title: "Order status updated",
        description: `Order ${pending.order.confirmationNumber} is now ${pending.target}.`,
      });
      setPending(null);
    } catch {
      // Axios response interceptor already surfaces a destructive toast.
    } finally {
      setUpdating(false);
    }
  }, [dispatch, pending, toast]);

  return {
    pending,
    updating,
    requestStatusUpdate,
    confirmStatusUpdate,
    cancelStatusUpdate,
  };
}